import React from "react";
import { motion } from "framer-motion";
import { useScrollAnimation } from "../hooks/useScrollAnimation";
import Navbar from "../components/landing/Navbar";
import Footer from "../components/landing/Footer";

const AboutUs = () => {
  const [ref, isVisible] = useScrollAnimation(0.1);

  const caseStudies = [
    {
      company: "TechCorp Inc.",
      industry: "Technology",
      challenge:
        "Manual customer onboarding process taking 3-5 days per customer",
      solution:
        "Deployed HR Recruiter Assistant and Customer Support Assistant",
      results:
        "95% reduction in onboarding time, 300% increase in customer satisfaction",
      logo: "T",
    },
    {
      company: "SalesForce Pro",
      industry: "Sales & Marketing",
      challenge:
        "Lead qualification bottleneck with 70% of leads going unprocessed",
      solution:
        "Implemented Sales Representative Assistant and Outbound Sales Assistant",
      results:
        "85% increase in qualified leads, 200% boost in conversion rates",
      logo: "S",
    },
    {
      company: "Global Support Co.",
      industry: "Customer Service",
      challenge: "24/7 support coverage needed across multiple time zones",
      solution:
        "Deployed Customer Support Assistant with multi-language capabilities",
      results:
        "99% uptime achieved, 60% reduction in response time, 24/7 coverage",
      logo: "G",
    },
  ];

  const stats = [
    { value: "50+", label: "AI Agents Deployed" },
    { value: "2×", label: "Faster Operations" },
    { value: "40%", label: "Cost Reduction" },
    { value: "Built-in", label: "AI Guardrails" },
  ];

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-6">
          <motion.div
            ref={ref}
            initial={{ opacity: 0, y: 30 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <motion.span
              initial={{ opacity: 0, scale: 0.9 }}
              animate={isVisible ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="inline-block px-4 py-2 mb-6 text-sm font-semibold text-primary-700 bg-primary-100 rounded-full"
            >
              About Us
            </motion.span>
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              AI First. Human Always.
              <span className="text-gradient"> Intelligence Operations</span>
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              We help SMEs and startups adopt AI responsibly and build intelligent operations that scale profitably.
              Our AI-in-the-Loop approach combines artificial intelligence with human oversight to create safer,
              workflows and drive unprecedented operational efficiency.
              Cultivate your digital garden with specialized agents.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={isVisible ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-4xl md:text-5xl font-bold text-gradient mb-2">
                  {stat.value}
                </div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Section */}
      <section className="py-24 bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Our{" "}
              <span className="text-gradient">Mission & Vision</span>
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed mb-6">
              <strong>Mission:</strong> To help SMEs and startups adopt AI responsibly and build intelligent operations that scale profitably.
            </p>
            <p className="text-xl text-gray-600 leading-relaxed mb-6">
              <strong>Vision:</strong> A future where AI and humans work together to create smarter, safer, and more productive organizations.
            </p>
            <p className="text-xl text-gray-600 leading-relaxed">
              We combine artificial intelligence with human oversight — ensuring AI operates safely, accurately, and alongside people, not instead of them.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={isVisible ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <h3 className="text-3xl font-bold text-gray-900 mb-6">
                Our Core Values
              </h3>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-accent-500 rounded-xl flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-xl font-bold">AI</span>
                  </div>
                  <div>
                    <h4 className="text-xl font-semibold text-gray-900 mb-2">
                      Intelligence
                    </h4>
                    <p className="text-gray-600">
                      AI as a multiplier. We believe artificial intelligence should amplify human capabilities,
                      not replace them. Our agents work alongside your team to enhance productivity and decision-making.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-accent-500 to-primary-500 rounded-xl flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-xl font-bold">SME</span>
                  </div>
                  <div>
                    <h4 className="text-xl font-semibold text-gray-900 mb-2">
                      Inclusion
                    </h4>
                    <p className="text-gray-600">
                      Accessible for SMEs. Advanced AI shouldn't be exclusive to enterprise companies.
                      We make intelligent operations affordable, simple, and scalable for startups and small-to-medium businesses.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-accent-500 rounded-xl flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-xl font-bold">UK</span>
                  </div>
                  <div>
                    <h4 className="text-xl font-semibold text-gray-900 mb-2">
                      Responsibility
                    </h4>
                    <p className="text-gray-600">
                      Safe, compliant, ethical AI. Built with UK AI standards and aligned with the EU AI Act.
                      Human oversight is built in for accuracy, governance, and risk control.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={isVisible ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="relative"
            >
              <div className="glass-effect rounded-3xl p-8 shadow-2xl">
                <h4 className="text-2xl font-bold text-gray-900 mb-6">
                  Competitive Advantages
                </h4>
                <div className="space-y-6">
                  {[
                    {
                      icon: "RD",
                      title: "Rapid Deployment",
                      description:
                        "Deploy AI agents in minutes vs months for competitors",
                    },
                    {
                      icon: "PT",
                      title: "Proven Templates",
                      description:
                        "Pre-built solutions for common business workflows",
                    },
                    {
                      icon: "NC",
                      title: "No-Code Platform",
                      description:
                        "Business users can deploy without technical expertise",
                    },
                    {
                      icon: "SA",
                      title: "Scalable Architecture",
                      description:
                        "Built to handle enterprise-scale deployments",
                    },
                  ].map((advantage, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: 30 }}
                      animate={isVisible ? { opacity: 1, x: 0 } : {}}
                      transition={{ duration: 0.5, delay: 0.6 + index * 0.1 }}
                      className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-md"
                    >
                      <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-accent-500 rounded-lg flex items-center justify-center flex-shrink-0">
                        <span className="text-white text-xs font-bold">
                          {advantage.icon}
                        </span>
                      </div>
                      <div>
                        <h5 className="font-semibold text-gray-900 mb-1">
                          {advantage.title}
                        </h5>
                        <p className="text-gray-600 text-sm">
                          {advantage.description}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Case Studies Section */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Success <span className="text-gradient">Stories</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our customers across diverse industries are achieving measurable
              ROI through rapid automation deployment, demonstrating strong
              market validation and growth potential.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-3 gap-8">
            {caseStudies.map((study, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                animate={isVisible ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="glass-effect rounded-2xl p-8 hover:shadow-xl transition-all duration-300"
              >
                <div className="text-4xl mb-4">{study.logo}</div>
                <div className="mb-4">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    {study.company}
                  </h3>
                  <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 text-sm font-semibold rounded-full">
                    {study.industry}
                  </span>
                </div>

                <div className="mb-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">
                    Challenge:
                  </h4>
                  <p className="text-gray-600 text-sm mb-4">
                    {study.challenge}
                  </p>

                  <h4 className="text-lg font-semibold text-gray-900 mb-2">
                    Solution:
                  </h4>
                  <p className="text-gray-600 text-sm mb-4">{study.solution}</p>

                  <h4 className="text-lg font-semibold text-gray-900 mb-2">
                    Results:
                  </h4>
                  <p className="text-primary-600 font-semibold text-sm">
                    {study.results}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-primary-600 to-accent-600">
        <div className="container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to Transform Your Business?
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Join thousands of businesses already using AgentGarden to
              cultivate their digital gardens and scale their operations.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                className="px-8 py-4 bg-white text-primary-600 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all"
              >
                Start Building Free
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                className="px-8 py-4 bg-transparent text-white border-2 border-white rounded-xl font-semibold hover:bg-white hover:text-primary-600 transition-all"
              >
                Schedule Demo
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default AboutUs;
